/* eslint-disable react-refresh/only-export-components -- hooks and helpers co-located with ScrollManager component */
import { useEffect, useRef, useLayoutEffect } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * ScrollManager Component
 * 
 * Smooth scroll management without visual "ticks":
 * - PUSH navigation: Scroll to top instantly during page transition
 * - POP navigation (back/forward): Restore saved position smoothly
 * - Uses a solid overlay to hide content during scroll restoration
 * 
 * Flow for PUSH: Old page fades out → Scroll to top → New page fades in
 * Flow for POP: Old page fades out → Restore scroll → Wait for paint → Fade in
 */

const SCROLL_STORAGE_KEY = 'yim_scroll_positions';

// Debounce helper
function debounce<T extends (...args: unknown[]) => void>(fn: T, delay: number): T {
  let timeoutId: ReturnType<typeof setTimeout>;
  return ((...args: unknown[]) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  }) as T;
}

// Get stored scroll positions
function getScrollPositions(): Record<string, number> {
  try {
    const stored = sessionStorage.getItem(SCROLL_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

// Save scroll position for a path
function saveScrollPosition(pathname: string, scrollY: number): void {
  try {
    const positions = getScrollPositions();
    positions[pathname] = scrollY;
    
    // Keep only last 30 entries
    const keys = Object.keys(positions);
    if (keys.length > 30) {
      const oldest = keys[0];
      delete positions[oldest];
    }
    
    sessionStorage.setItem(SCROLL_STORAGE_KEY, JSON.stringify(positions));
  } catch {
    // Silently fail
  }
}

// Get saved scroll position for a path
function getSavedScrollPosition(pathname: string): number | null {
  const positions = getScrollPositions();
  return positions[pathname] ?? null;
}

interface ScrollManagerProps {
  children: React.ReactNode;
  /** Enable page transition animations */
  enableTransitions?: boolean;
}

export function ScrollManager({ 
  children, 
  enableTransitions = true,
}: ScrollManagerProps) {
  const location = useLocation();
  const navigationType = useNavigationType();
  const previousPathnameRef = useRef<string | null>(null);
  const isRestoringRef = useRef(false);
  const navigationTypeRef = useRef<string>(navigationType);

  // Update navigation type ref
  useLayoutEffect(() => {
    navigationTypeRef.current = navigationType;
  }, [navigationType]);

  // Save scroll position on scroll (debounced)
  useEffect(() => {
    const handleScroll = debounce(() => {
      // Don't save while restoring
      if (isRestoringRef.current) return;
      
      const currentPath = window.location.pathname;
      saveScrollPosition(currentPath, window.scrollY);
    }, 100);

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle scroll on route change
  useLayoutEffect(() => {
    const currentPath = location.pathname;
    const previousPath = previousPathnameRef.current;
    
    // Skip if same path (hash change, etc.)
    if (currentPath === previousPath) {
      return;
    }

    if (navigationType === 'POP') {
      // Back/Forward navigation: Restore position immediately (no animation)
      const savedPosition = getSavedScrollPosition(currentPath);
      const pos = savedPosition ?? 0;
      
      isRestoringRef.current = true;
      window.scrollTo({ top: pos, left: 0, behavior: 'instant' as ScrollBehavior });
      
      // Reset restoring flag after a frame
      requestAnimationFrame(() => {
        isRestoringRef.current = false;
      });
    } else if (navigationType === 'PUSH') {
      // New navigation: Scroll to top
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
    }
    // REPLACE: Don't change scroll position

    previousPathnameRef.current = currentPath;
  }, [location.pathname, navigationType]);

  // Save position on unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      saveScrollPosition(location.pathname, window.scrollY);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [location.pathname]);

  // No transitions mode or POP navigation - render without animation
  if (!enableTransitions) {
    return <>{children}</>;
  }

  // For POP navigation (back/forward), skip animation entirely
  const isPop = navigationType === 'POP';

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={location.pathname}
        initial={isPop ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={isPop ? { opacity: 1 } : { opacity: 0 }}
        transition={{ 
          duration: isPop ? 0 : 0.15,
          ease: 'easeOut'
        }}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
}

/**
 * ScrollToTop Component
 * 
 * Standalone component for layouts without AnimatePresence.
 * Uses useLayoutEffect for synchronous scroll before paint.
 */
export function ScrollToTop() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const previousPathnameRef = useRef<string | null>(null);

  useLayoutEffect(() => {
    const currentPath = location.pathname;
    
    // Skip if same path
    if (currentPath === previousPathnameRef.current) {
      return;
    }

    // Only scroll to top on PUSH navigation
    if (navigationType === 'PUSH') {
      window.scrollTo({ 
        top: 0, 
        left: 0, 
        behavior: 'instant' as ScrollBehavior 
      });
    }

    previousPathnameRef.current = currentPath;
  }, [location.pathname, navigationType]);

  return null;
}

/**
 * useScrollToTop hook
 * Simple hook for components that need to scroll to top on mount
 */
export function useScrollToTop() {
  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
  }, []);
}

/**
 * useSaveScrollPosition hook
 * 
 * Manually save scroll position before navigation.
 * Use this on links like "View Recap" to ensure position is saved
 * before navigating away.
 * 
 * Usage:
 * const { saveBeforeNavigate } = useSaveScrollPosition();
 * <Link to="/recap" onClick={saveBeforeNavigate}>View Recap</Link>
 */
export function useSaveScrollPosition() {
  const location = useLocation();
  
  const saveBeforeNavigate = () => {
    saveScrollPosition(location.pathname, window.scrollY);
  };

  return { saveBeforeNavigate };
}

export { saveScrollPosition };

export default ScrollManager;
